/**
 * Agent Memory Schemas — Sprint 6 (L4 entity + L5 tenant persistent memory)
 *
 * All types derive from Zod schemas — no separate TypeScript interfaces
 * per CLAUDE.md §20.6.
 *
 * SCOPE: L4 (entity:customer, entity:deal, entity:user, entity:workflow) +
 *        L5 (tenant) memory layers for the Stateful Workflow Platform.
 *
 * NOT IN SCOPE: Ask Argus L3 conversation memory (agent_memories table in
 *               memory.ts). These are entirely separate systems.
 *
 * v1.1 §B CRIT-2 compliance: ObserveArgsSchema is STRICT — unknown fields
 * (including skip_approval) cause ZodError at parse time.
 */

import { z } from 'zod';
import { SensitivitySchema } from './document.schema.js';

// ── Classification (shared building block) ───────────────────────────────────

export const ClassificationSchema = z.object({
  sensitivity: SensitivitySchema.default('unclassified'),
  exportControl: z.enum(['none', 'ITAR', 'EAR']).default('none'),
});
export type Classification = z.infer<typeof ClassificationSchema>;

// ── Scope / Layer enums ───────────────────────────────────────────────────────

/**
 * The allowed values for the scope_kind column in agent_memory_facts.
 * Maps to the memory "layers" concept from STATEFUL-WORKFLOW-PLATFORM-SPEC v1.0 §6.
 */
export const MemoryScopeKindSchema = z.enum([
  'tenant',           // L5 — tenant-wide defaults, admin-curated
  'entity:customer',  // L4 — entity-scoped: customer
  'entity:deal',      // L4 — entity-scoped: deal/opportunity
  'entity:user',      // L4 — entity-scoped: user (external contact)
  'entity:workflow',  // L4 — entity-scoped: workflow run context
  'conversation',     // L3 boundary — recorded here for completeness but
                      // conversation memory is served by existing agent_memories table
]);
export type MemoryScopeKind = z.infer<typeof MemoryScopeKindSchema>;

/**
 * Full memory layer taxonomy including non-persistent layers (L0-L2).
 * Used by AgentDefinition.memory_layers to declare which layers an agent uses.
 */
export const MemoryLayerSchema = z.enum([
  'invocation',       // L1 — in-RAM, agent_executions.steps_jsonb
  'workflow_run',     // L2 — workflow_step_executions.output
  'conversation',     // L3 — existing agent_memories (Ask Argus table, not new)
  'entity:customer',  // L4
  'entity:deal',      // L4
  'entity:user',      // L4
  'entity:workflow',  // L4
  'tenant',           // L5
]);
export type MemoryLayer = z.infer<typeof MemoryLayerSchema>;

// ── ObserveArgsSchema (strict — closes CRIT-2 skip_approval bypass) ──────────

/**
 * Input schema for the memory.observe MCP tool.
 *
 * SECURITY: .strict() ensures ANY unrecognized field causes a ZodError.
 * This directly closes CRIT-2: { ...validArgs, skip_approval: true }
 * will throw at ZodError at Zod.parse time, before reaching any handler.
 *
 * AC-4: assert.throws(() => ObserveArgsSchema.parse({ ...validArgs, skip_approval: true }))
 */
export const ObserveArgsSchema = z.object({
  scope_kind: MemoryScopeKindSchema,
  scope_key: z.string().max(255).nullable(),
  memory_key: z.string().min(1).max(120),
  value: z.unknown(),
  confidence: z.number().min(0).max(1),
  evidence: z.string().min(1).max(2000),
}).strict();
// NOTE: .strict() means { ...args, skip_approval: true } → ZodError
// This is the primary test-contract for acceptance criterion AC-4.
export type ObserveArgs = z.infer<typeof ObserveArgsSchema>;

// ── AgentMemoryFactRecord ─────────────────────────────────────────────────────

export const AgentMemoryFactRecordSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  agentSlug: z.string().max(120),
  scopeKind: MemoryScopeKindSchema,
  scopeKey: z.string().max(255).nullable(),
  memoryKey: z.string().max(120),
  memoryValue: z.unknown(),
  confidence: z.number().min(0).max(1),
  observationCount: z.number().int().min(1),
  contradictionCount: z.number().int().min(0),
  firstObservedAt: z.date(),
  lastObservedAt: z.date(),
  expiresAt: z.date().nullable(),
  supersededAt: z.date().nullable(),
  supersededById: z.string().uuid().nullable(),
  writtenByInvocationId: z.string().uuid().nullable(),
  writtenByWorkflowRunId: z.string().uuid().nullable(),
  writtenByUserId: z.string().uuid().nullable(),
  classificationJsonb: ClassificationSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type AgentMemoryFactRecord = z.infer<typeof AgentMemoryFactRecordSchema>;

// ── AgentMemoryObservationRecord ──────────────────────────────────────────────

export const AgentMemoryObservationRecordSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  agentSlug: z.string().max(120),
  scopeKind: MemoryScopeKindSchema,
  scopeKey: z.string().max(255).nullable(),
  memoryKey: z.string().max(120),
  observedValue: z.unknown(),
  observedConfidence: z.number().min(0).max(1),
  observedAt: z.date(),
  writtenByInvocationId: z.string().uuid().nullable(),
  writtenByWorkflowRunId: z.string().uuid().nullable(),
  evidence: z.string().max(2000).nullable(),
  agreementWithExisting: z.boolean().nullable(),
  classificationJsonb: ClassificationSchema.nullable(),
});
export type AgentMemoryObservationRecord = z.infer<typeof AgentMemoryObservationRecordSchema>;

// ── AgentPromptContext ────────────────────────────────────────────────────────

export const AgentContextSectionSchema = z.object({
  title: z.string(),
  content: z.string(),
});
export type AgentContextSection = z.infer<typeof AgentContextSectionSchema>;

export const AgentPromptContextSchema = z.object({
  systemPrompt: z.string(),
  sections: z.array(AgentContextSectionSchema),
  /**
   * Rendered string ready for LLM injection.
   * Assembled by renderAgentContext() in the service.
   * Each section becomes: \n# <title>\n<content>
   */
  rendered: z.string(),
  /**
   * Total estimated token count across all sections.
   * Used for context budget management (Section 7 of spec).
   */
  estimatedTokens: z.number().int(),
});
export type AgentPromptContext = z.infer<typeof AgentPromptContextSchema>;

// ── Tenant memory config ──────────────────────────────────────────────────────

export const TenantMemoryConfigSchema = z.object({
  agentMemoryCrossAgentDefaultOn: z.boolean().default(true),
});
export type TenantMemoryConfig = z.infer<typeof TenantMemoryConfigSchema>;

// ── Memory visibility (per agent definition) ──────────────────────────────────

export const MemoryVisibilitySchema = z.enum(['shared', 'private']);
export type MemoryVisibility = z.infer<typeof MemoryVisibilitySchema>;

// ── Entity links convention ───────────────────────────────────────────────────

/**
 * Agent outputs may include entity_links to propagate entity IDs to downstream
 * agents. Shape: { "customer": "uuid-acme", "deal": "uuid-deal-42" }
 * Per v1.1 §B §5.3 step 6 (entity_ids propagation).
 */
export const EntityLinksSchema = z.record(z.string(), z.string());
export type EntityLinks = z.infer<typeof EntityLinksSchema>;

// ── MemoryObserveResult ───────────────────────────────────────────────────────

export const MemoryObserveResultSchema = z.object({
  status: z.enum(['written', 'pending_approval', 'updated', 'superseded']),
  factId: z.string().uuid().nullable(),
  approvalId: z.string().uuid().nullable(),
  message: z.string(),
  agreement: z.boolean().nullable(),
});
export type MemoryObserveResult = z.infer<typeof MemoryObserveResultSchema>;

// ── AgentInvocation (passed to service methods) ───────────────────────────────

export const AgentInvocationSchema = z.object({
  id: z.string(),                       // agent_jobs.id or synthetic ID
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  agentSlug: z.string(),
  workflowRunId: z.string().uuid().optional(),
});
export type AgentInvocation = z.infer<typeof AgentInvocationSchema>;
